import { t } from "@travily/i18n";
import { formatNativePrice, formatPrice, getDisplayCurrency, isDisplayConverted } from "@ui/lib/format-price";

export interface PriceBreakdownProps {
  displayCurrency: string;
  displayPrice: string;
  nativePrice: string;
}

export function PriceBreakdown({ displayCurrency, displayPrice, nativePrice }: PriceBreakdownProps) {
  return (
    <div className="flex w-56 flex-col gap-3">
      <p className="text-sm font-semibold text-neutral-900">
        {t("components.views.shared.listing_card.price_details")}
      </p>
      <div className="flex items-center justify-between gap-4 text-sm text-neutral-600">
        <span>{t("components.views.shared.listing_card.listed_price")}</span>
        <span className="shrink-0 font-medium text-neutral-900">{nativePrice}</span>
      </div>
      <hr className="border-neutral-100" />
      <div className="flex items-center justify-between gap-4 text-sm text-neutral-600">
        <span>{t("components.views.shared.listing_card.approximate_in", { currency: displayCurrency })}</span>
        {/* eslint-disable-next-line i18next/no-literal-string -- approximate symbol prefix */}
        <span className="shrink-0 font-medium text-neutral-900">~{displayPrice}</span>
      </div>
    </div>
  );
}

/** Resolves approximate price state for a listing with pricing data.
 *  Returns the approximate flag and hover content, or defaults when no conversion is active. */
export function resolveApproximatePrice(listing: {
  free?: boolean;
  price_cents?: number | null;
  price_currency?: string | null;
}): { approximate: boolean; priceHoverContent: React.ReactNode | undefined } {
  const { price_cents, price_currency } = listing;
  const approximate = !listing.free && price_currency != null && isDisplayConverted(price_currency);
  if (!approximate || price_cents == null || price_currency == null) {
    return { approximate: false, priceHoverContent: undefined };
  }
  return {
    approximate: true,
    priceHoverContent: (
      <PriceBreakdown
        displayCurrency={getDisplayCurrency() ?? "USD"}
        displayPrice={formatPrice(price_cents, price_currency)}
        nativePrice={formatNativePrice(price_cents, price_currency)}
      />
    ),
  };
}
